import { useSearchParams } from "react-router";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
	MARKETING_MATERIALS_SEARCH_KEY,
	MARKETING_MATERIALS_PAGE_KEY,
	MarketingMaterialsSearchButton,
	MarketingMaterialsSearchHelperText,
	MarketingMaterialsSearchPlaceholder,
	SORT_OPTIONS
} from "./marketingMaterialsConstants.ts";
import * as Style from "./MarketingMaterialsLandingPage.styles.ts";
import {
	MarketingMaterialsFilterOpts,
	MarketingMaterialsToolbar
} from "./components/MarketingMaterialsToolbar/MarketingMaterialsToolbar.tsx";
import useSession from "hooks/useSession.ts";
import { RequestCacheKeys } from "pages/CustomQuote/customQuoteConstants.ts";
import { ProductSearchResults } from "./components/ProductSearchResults/ProductSearchResults.tsx";
import { PageHeader } from "features/MarketingMaterials/pages/components/PageHeader/PageHeader.tsx";
import useMarketingMaterialsCart from "../hooks/useMarketingMaterialsCart.ts";
import { useGetMerchandiseFiltersQuery, useLazyGetMerchandiseProductsQuery } from "../api/merchProductsApi.ts";
import { buildMerchandiseProductRequest } from "../utils/buildMerchandiseProductRequest.ts";
import { MerchandiseProductFilterRequest } from "../api/getMerchandiseProducts.ts";
import { MarketingMaterialsSearchBar } from "./components/MarketingMaterialsSearchBar/MarketingMaterialsSearchBar.tsx";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { MarketingMaterialsNewOrderBannerPrefixText, PlaceANewOrderText } from "constants/text";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { UserChannel } from "types/declarations/enums.ts";

const MarketingMaterialsLandingPage = () => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const { data: userDetails } = useGetUserDetailsQuery();
	const [searchParams, setSearchParams] = useSearchParams();
	const [searchValue, setSearchValue] = useSession<string>(MARKETING_MATERIALS_SEARCH_KEY, "");
	const lastHandledUrlSearchRef = useRef<string | null>(null);
	const [currentPage, setCurrentPage] = useSession<number>(MARKETING_MATERIALS_PAGE_KEY, 1);
	const [filterOptions, setFilterOptions] = useSession<MarketingMaterialsFilterOpts>(
		RequestCacheKeys.RequestFilters,
		MarketingMaterialsFilterOptions
	);
	const lastRequestRef = useRef<MerchandiseProductFilterRequest | null>(null);
	const { cartData, addCartItem } = useMarketingMaterialsCart();
	const isMarketingMaterialsBannerEnabled =
		configurationsData?.featureFlags?.marketingMaterialsBannerEnabled === true;
	const isMenardsUser = userDetails?.userChannel === UserChannel.MENARDS;
	const shouldShowNewOrderBanner = isMarketingMaterialsBannerEnabled && !isMenardsUser;

	const { data: merchandiseFilters } = useGetMerchandiseFiltersQuery();

	const [trigger, { data: merchandise, isLoading: isLoadingMerchandise, isUninitialized }] =
		useLazyGetMerchandiseProductsQuery();

	const merchandiseSearch = useCallback(
		(nextFilterOptions: MarketingMaterialsFilterOpts, pageNumber: number, term: string) => {
			if (!merchandiseFilters) return;

			const request = buildMerchandiseProductRequest({
				searchValue: term,
				pageNumber: pageNumber,
				pageSize: 120,
				filterOptions: nextFilterOptions,
				merchandiseFilters
			});

			lastRequestRef.current = request;

			setCurrentPage(pageNumber);
			trigger(request);
		},
		[merchandiseFilters, setCurrentPage, trigger]
	);

	const onPageChange = useCallback(
		(nextPage: number) => {
			if (!lastRequestRef.current) return;

			const urlTerm = searchParams.get("term") ?? "";
			lastHandledUrlSearchRef.current = `${urlTerm}|${nextPage}`;

			const nextRequest = {
				...lastRequestRef.current,
				pageNumber: nextPage
			};

			lastRequestRef.current = nextRequest;
			setCurrentPage(nextPage);
			const nextParams = new URLSearchParams(searchParams);
			if (nextPage > 1) nextParams.set("page", String(nextPage));
			else nextParams.delete("page");
			setSearchParams(nextParams);
			trigger(nextRequest);
		},
		[searchParams, setCurrentPage, setSearchParams, trigger]
	);

	const onFiltersChanged = useCallback(
		(nextFilterOptions: MarketingMaterialsFilterOpts) => {
			const urlTerm = searchParams.get("term") ?? "";
			lastHandledUrlSearchRef.current = `${urlTerm}|1`;
			setCurrentPage(1);

			const nextParams = new URLSearchParams(searchParams);
			nextParams.delete("page");
			setSearchParams(nextParams);
			merchandiseSearch(nextFilterOptions, 1, searchValue);
		},
		[merchandiseSearch, searchParams, searchValue, setCurrentPage, setSearchParams]
	);

	const setSearchTermInUrl = useCallback(
		(term: string) => {
			const next = new URLSearchParams(searchParams);
			const trimmed = term.trim();

			if (trimmed) next.set("term", trimmed);
			else next.delete("term");

			setSearchParams(next);
		},
		[searchParams, setSearchParams]
	);

	const handleSearch = useCallback(() => {
		const next = new URLSearchParams(searchParams);
		const trimmed = searchValue.trim();
		setCurrentPage(1);

		if (trimmed) next.set("term", trimmed);
		else next.delete("term");
		next.delete("page");

		setSearchParams(next);
	}, [searchParams, searchValue, setCurrentPage, setSearchParams]);

	// Hydrate search term/page from url for bookmark, back button, etc.
	useEffect(() => {
		if (!merchandiseFilters) return;

		const urlTerm = searchParams.get("term") ?? "";
		const urlPageRaw = Number(searchParams.get("page"));
		const urlPage = Number.isInteger(urlPageRaw) && urlPageRaw > 0 ? urlPageRaw : 1;
		const currentUrlSearchKey = `${urlTerm}|${urlPage}`;

		// First load: if URL has no term, but session has one, push it into URL and wait
		// for the URL change to drive search (keeps URL/source of truth consistent).
		if (lastHandledUrlSearchRef.current === null && !urlTerm && searchValue.trim()) {
			setSearchTermInUrl(searchValue);
			return;
		}

		// Prevent duplicate searches for same URL term+page when other deps change.
		if (lastHandledUrlSearchRef.current === currentUrlSearchKey) return;
		lastHandledUrlSearchRef.current = currentUrlSearchKey;

		if (searchValue !== urlTerm) {
			setSearchValue(urlTerm);
		}

		merchandiseSearch(filterOptions, urlPage, urlTerm);
	}, [
		searchParams,
		merchandiseFilters,
		searchValue,
		setSearchValue,
		setSearchTermInUrl,
		merchandiseSearch,
		filterOptions
	]);

	const resetSearch = useCallback(() => {
		setSearchValue("");
		setCurrentPage(1);
		const next = new URLSearchParams(searchParams);
		next.delete("term");
		next.delete("page");
		setSearchParams(next);
	}, [searchParams, setCurrentPage, setSearchParams, setSearchValue]);

	return (
		<>
			<Style.MarketingMaterialsPageWrapper data-testid="marketing-materials-landing-page">
				<PageHeader
					title="Marketing Materials"
					cartData={cartData}
				/>
				{shouldShowNewOrderBanner && (
					<Style.MarketingMaterialsAnnouncementBanner
						to="/new-order"
						prefixText={MarketingMaterialsNewOrderBannerPrefixText}
						actionText={PlaceANewOrderText}
						dataTestId="marketing-materials-new-order-banner"
					/>
				)}

				<MarketingMaterialsSearchBar
					value={searchValue}
					placeholder={MarketingMaterialsSearchPlaceholder}
					helperText={MarketingMaterialsSearchHelperText}
					searchButtonLabel={MarketingMaterialsSearchButton}
					onChange={setSearchValue}
					onSearch={handleSearch}
					onClear={resetSearch}
				/>
				<Style.MarketingMaterialsLandingPageContent>
					<Style.MarketingMaterialsListFiltersContainer>
						<MarketingMaterialsToolbar
							filterOptions={filterOptions ?? {}}
							setFilterOptions={setFilterOptions ?? {}}
							onFiltersChanged={onFiltersChanged}
							merchandiseFilters={merchandiseFilters}
						/>
						{!isUninitialized && !isLoadingMerchandise && (
							<Style.ResultsCount>Showing {merchandise?.totalCount ?? 0} results</Style.ResultsCount>
						)}
					</Style.MarketingMaterialsListFiltersContainer>
					<ProductSearchResults
						searchResults={merchandise}
						cartData={cartData}
						pageNumber={currentPage}
						onPageChange={onPageChange}
						addCartItem={addCartItem}
					></ProductSearchResults>
				</Style.MarketingMaterialsLandingPageContent>
			</Style.MarketingMaterialsPageWrapper>
		</>
	);
};

export default MarketingMaterialsLandingPage;

export const MarketingMaterialsFilterOptions = {
	channel: [],
	brandAndProductLine: [],
	category: [],
	type: [],
	availability: [],
	sort: SORT_OPTIONS[0]!["value"]
};
